// components/AccountsTable.jsx
import React, { useState, useMemo, useRef, useCallback } from "react";
import { BsArrowDownUp } from "react-icons/bs";
import Draggable from "react-draggable";
import "./accounts.css"; // Create if needed

const AccountsTable = ({
  currentPosts = [],
  handleCheckboxChange,
  selectedIds = [],
  selectAll = false,
  handleSelectAll,
}) => {
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [columnWidths, setColumnWidths] = useState({
    id: 30,
    userid: 140,
    name: 120,
    account_no: 90,
    ifsc: 80,
    pan: 75,
    status: 50,
  });

  const resizingCol = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date)) return value;
    return date.toISOString().split("T")[0];
  };

  const uniquePosts = useMemo(() => {
    const seen = new Set();
    return currentPosts.filter((item) => {
      if (!item?.id) return false;
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }, [currentPosts]);

  const sortedData = useMemo(() => {
    if (!Array.isArray(uniquePosts) || uniquePosts.length === 0) return [];

    const arr = [...uniquePosts];
    if (sortColumn) {
      arr.sort((a, b) => {
        let vA = a[sortColumn];
        let vB = b[sortColumn];

        if (vA == null) return 1;
        if (vB == null) return -1;

        if (sortColumn.includes("date") || sortColumn.includes("created")) {
          vA = new Date(vA).getTime();
          vB = new Date(vB).getTime();
        }

        return sortDirection === "asc"
          ? vA < vB ? -1 : vA > vB ? 1 : 0
          : vA > vB ? -1 : vA < vB ? 1 : 0;
      });
    }

    return arr;
  }, [uniquePosts, sortColumn, sortDirection]);

  const handleSort = useCallback(
    (col) => {
      setSortColumn(col);
      setSortDirection((prev) =>
        sortColumn === col && prev === "asc" ? "desc" : "asc"
      );
    },
    [sortColumn]
  );

  const onMouseDown = useCallback(
    (e, colKey) => {
      resizingCol.current = colKey;
      startX.current = e.clientX;
      startWidth.current = columnWidths[colKey];
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [columnWidths]
  );

  const onMouseMove = useCallback((e) => {
    if (!resizingCol.current) return;
    const dx = e.clientX - startX.current;
    setColumnWidths((prev) => {
      const newWidth = Math.max(startWidth.current + dx, 50);
      return { ...prev, [resizingCol.current]: newWidth };
    });
  }, []);

  const onMouseUp = useCallback(() => {
    resizingCol.current = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }, [onMouseMove]);

  const columns = [
    { key: "id", label: "ID", align: "center", bold: true },
    { key: "userid", label: "UserID" },
    { key: "name", label: "Name" },
    { key: "account_no", label: "Account Number" },
    { key: "ifsc", label: "IFSC" },
    { key: "pan", label: "PAN" },
    { key: "status", label: "Status" },
    // { key: "created_at", label: "Created At", format: formatDate },
  ];

  return (
    <div className="table-responsive">
      <table className="table table-bordered">
        <thead>
          <tr>
            <th style={{ width: 30, minWidth: 30 }} className="text-center">
              <input
                type="checkbox"
                checked={
                  uniquePosts.length > 0 &&
                  uniquePosts.every((item) => selectedIds.includes(item.id))
                }
                onChange={handleSelectAll}
              />
            </th>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`text-${col.align || "left"} sortable`}
                style={{
                  width: columnWidths[col.key],
                  minWidth: 50,
                  position: "relative",
                  userSelect: "none",
                  cursor: "pointer",
                }}
                onClick={() => handleSort(col.key)}
              >
                <div className="d-flex align-items-center justify-content-center gap-1">
                  <BsArrowDownUp size={12} />
                  <span className={col.bold ? "fw-bold" : ""}>{col.label}</span>
                </div>
                <div
                  className="resize-handle"
                  onMouseDown={(e) => onMouseDown(e, col.key)}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: "5px",
                    cursor: "col-resize",
                    userSelect: "none",
                  }}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => (
            <tr
              key={row.id}
              style={{
                backgroundColor: selectedIds.includes(row.id) ? "#d0e1f9" : "",
              }}
            >
              <td className="text-center">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(row.id)}
                  onChange={() => handleCheckboxChange(row.id)}
                />
              </td>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`wrap-text-style text-${col.align || "left"} ${
                    col.bold ? "fw-bold" : ""
                  }`}
                  style={{
                    width: columnWidths[col.key],
                    minWidth: 50,
                  }}
                >
                  {col.format ? col.format(row[col.key]) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modal for detailed account info */}
      {selectedAccount && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1200 }}
        >
          <div className="modal-dialog modal-lg">
            <Draggable handle=".modal-header">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Account Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedAccount(null)}
                    aria-label="Close"
                  />
                </div>
                <div
                  className="modal-body"
                  style={{ maxHeight: "400px", overflowY: "auto" }}
                >
                  <ul className="list-group">
                    {Object.entries(selectedAccount).map(([key, value]) => (
                      <li
                        key={key}
                        className="list-group-item d-flex justify-content-between"
                      >
                        <strong>{key}</strong>
                        <span style={{ fontFamily: "monospace" }}>
                          {String(value)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedAccount(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </Draggable>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsTable;
